import { store } from 'shared/stores/core/decorators';
import { makeObservable } from 'shared/stores/core/make-observable';

@store('IA_MARKUP_STATE')
export class MarkupStore {
  state = {
    stateByIaKey: {}
  };

  getters = {
    markupState ({get}) {
      const {syntheticKey} = get('IA_STATE');
      if (!this.state.stateByIaKey[syntheticKey]) {
        this.state.stateByIaKey[syntheticKey] = createState();
      }
      return this.state.stateByIaKey[syntheticKey];
    }
  };

  remap = {
    IA_STATE_CHANGE: 'REFRESH_MARKUP_STATE',
    REGION_REMOVED: 'IA_HIDE_MARKUP',
    // These two are here to handle race condition if we get IA results before reloading layer annotations. It's a bit
    // heavy-handed; if we can rework annotations to use Vue we won't need to manually sync.
    LAYER_REINITIALIZED: 'IA_EVALUATE_MARKUP_VISIBILITY', // for updated region id's
    REGION_ADDED: 'IA_EVALUATE_MARKUP_VISIBILITY' // for new regions
  };

  REFRESH_MARKUP_STATE ({dispatch}) {
    const stateRef = this.getters.markupState;

    // Reset state. We don't cache state since the results view is reset when changing macros or images.
    stateRef.isShowAllMarkupRequested = false;
    stateRef.visibleMarkupId = null;

    dispatch('IA_EVALUATE_MARKUP_VISIBILITY');
  }

  // shows a single markup region and optionally centers on it.
  IA_SHOW_MARKUP ({dispatch, state}, {region, overwrite = true, reposition = false}) {
    const stateRef = this.getters.markupState;
    const {displayId} = region;

    if (overwrite || stateRef.visibleMarkupId === null) {
      stateRef.visibleMarkupId = displayId;
      dispatch('IA_EVALUATE_MARKUP_VISIBILITY');

      if (reposition) {
        dispatch('SELECT_REGION', {region});
      }
    }
  }

  // Hides a single markup region
  IA_HIDE_MARKUP ({dispatch}, {region}) {
    const stateRef = this.getters.markupState;
    const {displayId} = region;
    if (stateRef.visibleMarkupId === displayId) {
      stateRef.visibleMarkupId = null;
    }

    dispatch('IA_EVALUATE_MARKUP_VISIBILITY');
  }

  // Toggles the selected markup region and optionally centers the view on it if shown.
  IA_TOGGLE_MARKUP ({dispatch}, {region, reposition}) {
    const stateRef = this.getters.markupState;
    const event = (stateRef.visibleMarkupId !== region.displayId) ? 'IA_SHOW_MARKUP' : 'IA_HIDE_MARKUP';
    dispatch(event, {region, reposition});
  }

  // Shows all markup on the current layer
  IA_SHOW_LAYER_MARKUP ({state, dispatch}) {
    const stateRef = this.getters.markupState;

    stateRef.visibleMarkupId = null;
    stateRef.isShowAllMarkupRequested = true;

    dispatch('IA_EVALUATE_MARKUP_VISIBILITY');
  }

  // Hides all the markup on the current layer
  IA_HIDE_LAYER_MARKUP ({dispatch}) {
    const stateRef = this.getters.markupState;

    stateRef.isShowAllMarkupRequested = false;

    dispatch('IA_EVALUATE_MARKUP_VISIBILITY');
  }

  // Toggles all markup for the current layer
  IA_TOGGLE_LAYER_MARKUP ({dispatch}) {
    const stateRef = this.getters.markupState;
    const event = stateRef.isShowAllMarkupRequested ? 'IA_HIDE_LAYER_MARKUP' : 'IA_SHOW_LAYER_MARKUP';
    dispatch(event);
  }

  // reviews the state object and shows/hides the markup as needed
  IA_EVALUATE_MARKUP_VISIBILITY ({get}) {
    const stateRef = this.getters.markupState;
    const displayIds = [];
    const {iaOutputLayer} = get('IA_ANNOTATIONS');
    const markupService = get('VIEWPORT').plugins('us:annotations:markup');

    // When switching images sometimes this is called before the markup service is available (the LAYERS_LOADED event
    // handles this condition).
    if (!markupService) {
      return;
    }
    markupService.hideAllRegions();

    // Sometimes there just isn't an output layer selected (because it hasn't been created yet).
    if (!iaOutputLayer) {
      return;
    }

    if (stateRef.visibleMarkupId !== null) {
      displayIds.push(stateRef.visibleMarkupId);
    } else if (stateRef.isShowAllMarkupRequested) {
      displayIds.push(...iaOutputLayer.regions.map(r => r.displayId));
    }

    // Convert displayId's to markupIds
    const ids = iaOutputLayer.regions.filter(r => displayIds.includes(r.displayId)).map(r => r.displayId);
    markupService.showRegions(ids, iaOutputLayer.id);
  }
}

function createState () {
  return makeObservable({
    visibleMarkupId: null,
    isShowAllMarkupRequested: false
  });
}



// WEBPACK FOOTER //
// ./src/shared/stores/image-analysis/annotations/MarkupStore.js